export type GitHubUser = {
    login: string;
    name: string | null;
    avatar_url: string;
    public_repos: number;
    public_gists: number;
    followers: number;
    following: number;
};

export type GitHubRepo = {
    id: number;
    name: string;
    full_name: string;
    html_url: string;
    description: string | null;
    language: string | null;
    stargazers_count: number;
    forks_count: number;
    updated_at: string;
    topics: string[];
};

export type GitHubEvent = {
    id: string;
    type: string;
    repo: { name: string };
    created_at: string;
    payload: {
        commits?: { message: string; sha: string }[];
        ref?: string;
        action?: string;
    };
};

const CACHE_KEY = "gh_dashboard_cache";
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

function getCached<T>(key: string): T | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = sessionStorage.getItem(`${CACHE_KEY}_${key}`);
        if (!raw) return null;
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts > CACHE_TTL) {
            sessionStorage.removeItem(`${CACHE_KEY}_${key}`);
            return null;
        }
        return data as T;
    } catch {
        return null;
    }
}

function setCache<T>(key: string, data: T) {
    if (typeof window === "undefined") return;
    try {
        sessionStorage.setItem(`${CACHE_KEY}_${key}`, JSON.stringify({ data, ts: Date.now() }));
    } catch { /* quota exceeded, ignore */ }
}

async function ghFetch<T>(path: string, cacheKey: string, token?: string | null): Promise<T | null> {
    const cached = getCached<T>(cacheKey);
    if (cached) return cached;

    try {
        const headers: HeadersInit = { Accept: "application/vnd.github.v3+json" };
        if (token) {
            headers.Authorization = `token ${token}`;
        }

        const res = await fetch(`https://api.github.com${path}`, { headers });
        if (!res.ok) return null;
        const data = await res.json();
        setCache(cacheKey, data);
        return data as T;
    } catch {
        return null;
    }
}

export function extractUsername(githubUrl: string): string | null {
    try {
        const url = new URL(githubUrl);
        const parts = url.pathname.split("/").filter(Boolean);
        return parts[0] || null;
    } catch {
        return null;
    }
}

export async function fetchGitHubUser(username: string, token?: string | null) {
    return ghFetch<GitHubUser>(`/users/${username}`, `user_${username}`, token);
}

export async function fetchGitHubRepos(username: string, token?: string | null) {
    return ghFetch<GitHubRepo[]>(
        `/users/${username}/repos?per_page=100&sort=updated&type=owner`,
        `repos_${username}`,
        token
    );
}

export async function fetchGitHubEvents(username: string, token?: string | null) {
    // Use /events (includes private) when authenticated, /events/public otherwise
    const path = token
        ? `/users/${username}/events?per_page=30`
        : `/users/${username}/events/public?per_page=30`;
    // Use different cache keys so authed fetch doesn't serve stale public-only data
    const cacheKey = token ? `events_auth_${username}` : `events_${username}`;
    return ghFetch<GitHubEvent[]>(path, cacheKey, token);
}

export async function fetchTotalCommits(username: string, token?: string | null): Promise<number> {
    try {
        const events = await fetchGitHubEvents(username, token);
        if (!events) return 0;

        // This is an estimate based on recent events since GitHub API doesn't provide a total commit count directly
        // A more accurate way would be search/commits but it requires auth and is heavy
        // For portfolio, we'll use a mix of heuristic or just stats from the last year if we had more API calls
        // For now, let's just return a decent number or use the user's public_repos * average commits if needed
        // Actually, let's try to fetch user stats via search API which is more accurate for "total"
        const headers: HeadersInit = { Accept: "application/vnd.github.v3+json" };
        if (token) headers.Authorization = `token ${token}`;

        const res = await fetch(`https://api.github.com/search/commits?q=author:${username}`, { headers });
        if (res.ok) {
            const data = await res.json();
            return data.total_count || 0;
        }
        return 0;
    } catch {
        return 0;
    }
}

export async function syncGitHubToDb(username: string, token?: string | null) {
    const [user, repos, events, commits] = await Promise.all([
        fetchGitHubUser(username, token),
        fetchGitHubRepos(username, token),
        fetchGitHubEvents(username, token),
        fetchTotalCommits(username, token),
    ]);

    if (!user || !repos) return null;

    const languages = aggregateLanguages(repos);
    const stars = totalStars(repos);
    const forks = totalForks(repos);

    const topRepos = [...repos]
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, 10)
        .map(r => ({
            name: r.name,
            description: r.description,
            html_url: r.html_url,
            language: r.language,
            stargazers_count: r.stargazers_count,
            forks_count: r.forks_count,
            updated_at: r.updated_at
        }));

    const recentEvents = (events || [])
        .slice(0, 10)
        .map(e => ({
            id: e.id,
            type: e.type,
            repo: e.repo.name,
            message: e.type === "PushEvent"
                ? (e.payload.commits?.[0]?.message || "Pushed changes")
                : e.type === "CreateEvent"
                    ? (e.payload.ref || "Created repository")
                    : e.type === "IssuesEvent"
                        ? (e.payload.action || "Issue activity")
                        : e.type === "PullRequestEvent"
                            ? (e.payload.action || "PR activity")
                            : (e.payload.action || "Activity"),
            created_at: e.created_at
        }));

    const cacheData = {
        username,
        public_repos: user.public_repos,
        total_stars: stars,
        total_forks: forks,
        followers: user.followers,
        total_commits: commits,
        top_languages: languages,
        top_repos: topRepos,
        recent_events: recentEvents,
        fetched_at: new Date().toISOString(),
    };

    const { createClient } = await import("@/lib/supabase");
    const supabase = createClient();

    const { data, error } = await supabase
        .from("github_cache")
        .upsert(cacheData, { onConflict: "username" })
        .select()
        .single();

    if (error) {
        // Log as warning to avoid triggering Next.js error overlay in dev
        console.warn("Error syncing GitHub stats:", error);
        return null;
    }

    return data;
}

export function aggregateLanguages(repos: GitHubRepo[]): { name: string; count: number; percent: number }[] {
    const langMap: Record<string, number> = {};
    for (const r of repos) {
        if (r.language) {
            langMap[r.language] = (langMap[r.language] || 0) + 1;
        }
    }
    const total = Object.values(langMap).reduce((a, b) => a + b, 0);
    return Object.entries(langMap)
        .sort(([, a], [, b]) => b - a)
        .map(([name, count]) => ({ name, count, percent: Math.round((count / total) * 100) }));
}

export function totalStars(repos: GitHubRepo[]): number {
    return repos.reduce((sum, r) => sum + r.stargazers_count, 0);
}

export function totalForks(repos: GitHubRepo[]): number {
    return repos.reduce((sum, r) => sum + r.forks_count, 0);
}
